import { Member, Contribution, Expense, Town } from '../types';

export interface PDFError {
  type: 'validation' | 'network' | 'generation' | 'permission' | 'unknown';
  message: string;
  details?: string;
  code?: string;
  timestamp: Date;
}

export interface PDFGenerationResult {
  success: boolean;
  data?: Blob;
  error?: PDFError;
  warnings?: string[];
}

export class PDFErrorHandler {
  private static instance: PDFErrorHandler;
  private errorLog: PDFError[] = [];
  private maxErrorLogSize = 100;

  static getInstance(): PDFErrorHandler {
    if (!PDFErrorHandler.instance) {
      PDFErrorHandler.instance = new PDFErrorHandler();
    }
    return PDFErrorHandler.instance;
  }

  /**
   * Validate input data for PDF generation
   */
  validateInputData(
    data: any[],
    reportType: 'members' | 'financial' | 'expenses' | 'events'
  ): PDFGenerationResult {
    try {
      // Check if data is empty
      if (!data || data.length === 0) {
        return {
          success: false,
          error: {
            type: 'validation',
            message: 'No data available for PDF generation',
            details: `The ${reportType} report has no data to export`,
            code: 'EMPTY_DATA',
            timestamp: new Date()
          }
        };
      }

      // Validate data structure
      const validationResult = this.validateDataStructure(data, reportType);
      if (!validationResult.success) {
        return validationResult;
      }

      // Check for required fields
      const requiredFieldsResult = this.validateRequiredFields(data, reportType);
      if (!requiredFieldsResult.success) {
        return requiredFieldsResult;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          type: 'validation',
          message: 'Input validation failed',
          details: error instanceof Error ? error.message : 'Unknown validation error',
          code: 'VALIDATION_ERROR',
          timestamp: new Date()
        }
      };
    }
  }

  /**
   * Validate data structure based on report type
   */
  private validateDataStructure(
    data: any[],
    reportType: 'members' | 'financial' | 'expenses' | 'events'
  ): PDFGenerationResult {
    const sampleItem = data[0];
    
    switch (reportType) {
      case 'members':
        if (!sampleItem.username || !sampleItem.name || !sampleItem.phone) {
          return {
            success: false,
            error: {
              type: 'validation',
              message: 'Invalid member data structure',
              details: 'Member records must contain username, name, and phone fields',
              code: 'INVALID_MEMBER_STRUCTURE',
              timestamp: new Date()
            }
          };
        }
        break;

      case 'financial':
        if (!sampleItem.amount || !sampleItem.date) {
          return {
            success: false,
            error: {
              type: 'validation',
              message: 'Invalid financial data structure',
              details: 'Financial records must contain amount and date fields',
              code: 'INVALID_FINANCIAL_STRUCTURE',
              timestamp: new Date()
            }
          };
        }
        break;

      case 'expenses':
        if (!sampleItem.amount || !sampleItem.category || !sampleItem.date) {
          return {
            success: false,
            error: {
              type: 'validation',
              message: 'Invalid expense data structure',
              details: 'Expense records must contain amount, category, and date fields',
              code: 'INVALID_EXPENSE_STRUCTURE',
              timestamp: new Date()
            }
          };
        }
        break;

      case 'events':
        if (!sampleItem.title || !sampleItem.date || !sampleItem.location) {
          return {
            success: false,
            error: {
              type: 'validation',
              message: 'Invalid event data structure',
              details: 'Event records must contain title, date, and location fields',
              code: 'INVALID_EVENT_STRUCTURE',
              timestamp: new Date()
            }
          };
        }
        break;
    }

    return { success: true };
  }

  /**
   * Validate required fields and data types
   */
  private validateRequiredFields(
    data: any[],
    reportType: 'members' | 'financial' | 'expenses' | 'events'
  ): PDFGenerationResult {
    const warnings: string[] = [];

    for (let i = 0; i < data.length; i++) {
      const item = data[i];

      // Check for null/undefined values
      Object.keys(item).forEach(key => {
        if (item[key] === null || item[key] === undefined) {
          warnings.push(`Record ${i + 1} has null value for field: ${key}`);
        }
      });

      // Type-specific validations
      switch (reportType) {
        case 'members':
          if (typeof item.amount === 'number' && item.amount < 0) {
            warnings.push(`Record ${i + 1} has negative amount: ${item.amount}`);
          }
          break;

        case 'financial':
          if (typeof item.amount === 'number' && item.amount < 0) {
            warnings.push(`Record ${i + 1} has negative amount: ${item.amount}`);
          }
          break;

        case 'expenses':
          if (typeof item.amount === 'number' && item.amount < 0) {
            warnings.push(`Record ${i + 1} has negative amount: ${item.amount}`);
          }
          break;
      }
    }

    return {
      success: true,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Handle PDF generation errors
   */
  handleGenerationError(error: any, context: string): PDFError {
    const pdfError: PDFError = {
      type: 'generation',
      message: 'PDF generation failed',
      details: error instanceof Error ? error.message : 'Unknown generation error',
      code: 'GENERATION_ERROR',
      timestamp: new Date()
    };

    // Log the error
    this.logError(pdfError);

    // Provide user-friendly error messages
    if (error?.message?.includes('network')) {
      pdfError.type = 'network';
      pdfError.message = 'Network error occurred during PDF generation';
      pdfError.code = 'NETWORK_ERROR';
    } else if (error?.message?.includes('permission')) {
      pdfError.type = 'permission';
      pdfError.message = 'Permission denied for PDF generation';
      pdfError.code = 'PERMISSION_ERROR';
    } else if (error?.message?.includes('memory')) {
      pdfError.type = 'generation';
      pdfError.message = 'PDF generation failed due to memory constraints';
      pdfError.code = 'MEMORY_ERROR';
    } else if (error?.message?.includes('timeout')) {
      pdfError.type = 'generation';
      pdfError.message = 'PDF generation timed out';
      pdfError.code = 'TIMEOUT_ERROR';
    }

    return pdfError;
  }

  /**
   * Handle chart rendering errors
   */
  handleChartError(error: any, chartType: string): PDFError {
    return {
      type: 'generation',
      message: `Chart rendering failed for ${chartType} chart`,
      details: error instanceof Error ? error.message : 'Unknown chart error',
      code: 'CHART_RENDER_ERROR',
      timestamp: new Date()
    };
  }

  /**
   * Handle template errors
   */
  handleTemplateError(error: any, templateName: string): PDFError {
    return {
      type: 'validation',
      message: `Template validation failed for ${templateName}`,
      details: error instanceof Error ? error.message : 'Unknown template error',
      code: 'TEMPLATE_ERROR',
      timestamp: new Date()
    };
  }

  /**
   * Log error for debugging and monitoring
   */
  private logError(error: PDFError): void {
    this.errorLog.push(error);
    
    // Maintain log size
    if (this.errorLog.length > this.maxErrorLogSize) {
      this.errorLog.shift();
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[PDF ERROR]', {
        type: error.type,
        message: error.message,
        details: error.details,
        code: error.code,
        timestamp: error.timestamp
      });
    }

    // In production, you might want to send errors to a monitoring service
    if (import.meta.env.PROD) {
      this.sendErrorToMonitoring(error);
    }
  }

  /**
   * Send error to monitoring service (placeholder implementation)
   */
  private sendErrorToMonitoring(error: PDFError): void {
    // Implementation would depend on your monitoring service
    // Examples: Sentry, LogRocket, custom error tracking
    console.log('[MONITORING] Sending error:', error);
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error: PDFError): string {
    switch (error.type) {
      case 'validation':
        return `Please check your data and try again. ${error.message}`;
      
      case 'network':
        return 'Please check your internet connection and try again.';
      
      case 'permission':
        return 'You do not have permission to generate this report. Please contact your administrator.';
      
      case 'generation':
        return 'PDF generation failed. Please try again or contact support if the problem persists.';
      
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Get error recovery suggestions
   */
  getRecoverySuggestions(error: PDFError): string[] {
    const suggestions: string[] = [];

    switch (error.type) {
      case 'validation':
        suggestions.push('Check that all required fields are filled');
        suggestions.push('Ensure data is in the correct format');
        suggestions.push('Verify that you have selected the correct report type');
        break;

      case 'network':
        suggestions.push('Check your internet connection');
        suggestions.push('Try again in a few moments');
        suggestions.push('Contact IT support if the problem persists');
        break;

      case 'permission':
        suggestions.push('Contact your system administrator');
        suggestions.push('Verify your user permissions');
        suggestions.push('Check if you need additional access rights');
        break;

      case 'generation':
        suggestions.push('Try generating a smaller report');
        suggestions.push('Clear your browser cache and try again');
        suggestions.push('Contact support with error code: ' + error.code);
        break;
    }

    return suggestions;
  }

  /**
   * Get error statistics for monitoring
   */
  getErrorStatistics(): {
    totalErrors: number;
    errorTypes: Record<string, number>;
    recentErrors: PDFError[];
  } {
    const errorTypes: Record<string, number> = {};
    
    this.errorLog.forEach(error => {
      errorTypes[error.type] = (errorTypes[error.type] || 0) + 1;
    });

    return {
      totalErrors: this.errorLog.length,
      errorTypes,
      recentErrors: this.errorLog.slice(-10)
    };
  }

  /**
   * Clear error log (useful for testing)
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }
}

// Export singleton instance
export const pdfErrorHandler = PDFErrorHandler.getInstance();

/**
 * Enhanced PDF generation wrapper with error handling
 */
export async function withPDFErrorHandling<T>(
  operation: () => Promise<T>,
  context: string
): Promise<PDFGenerationResult> {
  try {
    const result = await operation();
    return { success: true, data: result as any };
  } catch (error) {
    const pdfError = pdfErrorHandler.handleGenerationError(error, context);
    return { success: false, error: pdfError };
  }
}

/**
 * Validate and sanitize data before PDF generation
 */
export function validateAndSanitizeData(
  data: any[],
  reportType: 'members' | 'financial' | 'expenses' | 'events'
): PDFGenerationResult {
  const validator = pdfErrorHandler;
  
  // Validate input
  const validation = validator.validateInputData(data, reportType);
  if (!validation.success) {
    return validation;
  }

  // Sanitize data
  const sanitizedData = data.map(item => {
    const sanitized = { ...item };
    
    // Remove potentially dangerous content
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'string') {
        sanitized[key] = sanitized[key].replace(/[<>]/g, '');
      }
    });

    return sanitized;
  });

  return { success: true, data: sanitizedData as any };
}